import React from "react";
import { message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { PageHeader } from "../../components";
import { useWhatsAppService } from "../../Services";
import { ConnectionStatusCard } from "./common/ConnectionStatusCard";
import { HowItWorksCard } from "./common/HowItWorksCard";
import { StatsBar } from "./common/StatsBar";
import { ActivityLog } from "./common/ActivityLog";
import { ControlsCard } from "./common/ControlsCard";

export const WhatsAppChatbotPage = () => {
  const {
    gatewayStatusQuery,
    activityLogsQuery,
    logoutMutation,
    toggleMutation,
  } = useWhatsAppService();

  const status = gatewayStatusQuery.data?.status ?? "INITIALIZING";
  const qrValue = gatewayStatusQuery.data?.qr ?? null;
  const isLoading = gatewayStatusQuery.isLoading;
  const isConnected = status === "READY";

  const botEnabled = gatewayStatusQuery.data?.enabled ?? true;
  const uptimeSeconds = gatewayStatusQuery.data?.uptimeSeconds ?? null;
  const connectedAt = gatewayStatusQuery.data?.connectedAt ?? null;
  const totalMessages = gatewayStatusQuery.data?.totalMessages ?? 0;

  const logs = activityLogsQuery.data?.logs ?? [];

  const handleLogout = () => {
    logoutMutation.mutate(undefined, {
      onSuccess: (res) => {
        if (res.success) {
          message.success("WhatsApp disconnected. A new QR code will appear shortly.");
        } else {
          message.error(res.message || "Failed to disconnect.");
        }
      },
      onError: () => {
        message.error("Failed to send logout request.");
      },
    });
  };

  const handleToggle = (enabled: boolean) => {
    toggleMutation.mutate(enabled, {
      onSuccess: (res) => {
        if (res.success) {
          message.success(enabled ? "Bot enabled — now responding to messages." : "Bot paused — messages will be received but not replied.");
        } else {
          message.error("Failed to update bot state.");
        }
      },
    });
  };

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="WhatsApp Chatbot"
        subText="Manage and monitor your AI-powered WhatsApp integration"
        handleButtons={[
          {
            title: "Refresh Status",
            icon: ReloadOutlined,
            onClick: () => {
              gatewayStatusQuery.refetch();
              activityLogsQuery.refetch();
            },
            loading: isLoading,
          },
        ]}
      />

      {/* Stats Bar — shown only when connected */}
      {isConnected && (
        <div className="mt-4">
          <StatsBar
            totalMessages={totalMessages}
            uptimeSeconds={uptimeSeconds}
            connectedAt={connectedAt}
          />
        </div>
      )}

      {/* Top Row: Connection Status + Controls + How It Works */}
      <div className={`flex flex-col lg:flex-row gap-4`}>
        <div className="flex max-w-lg">
          <ConnectionStatusCard
            status={status}
            qrValue={qrValue}
            isLoading={isLoading}
          />
        </div>

        <div className="flex flex-col gap-4 flex-1 max-w-md">
          <ControlsCard
            isConnected={isConnected}
            botEnabled={botEnabled}
            isLoggingOut={logoutMutation.isPending}
            isToggling={toggleMutation.isPending}
            onLogout={handleLogout}
            onToggle={handleToggle}
          />
          <HowItWorksCard />
        </div>

        <div>
          {/* Activity Log — shown only when connected */}
      {isConnected && (
        <div>
          <ActivityLog
            logs={logs}
            loading={activityLogsQuery.isLoading}
          />
        </div>
      )}
        </div>
      </div>

      
    </div>
  );
};
